/**
 * Route-scoped context for the active customer / site-group / site.
 *
 * Provided by the scope Layout routes (CustomerLayout / SiteGroupLayout /
 * SiteLayout) once they have seeded and gated their tier. Descendant views read
 * the active scope via useScope() instead of importing raw Redux selectors, so
 * the Layouts are the single boundary between the (currently legacy) entity
 * store and the views.
 *
 * The Layout converts the entities to plain JS at that boundary, so views work
 * with plain objects — no immutable.js. When the data source moves off the
 * legacy store onto the odl-app contracts, only the Layouts change, not the
 * consumers.
 *
 * Global identity (user, permissions) stays in UserContext / useUser(); this
 * context holds only what changes as you navigate between customers, plants,
 * and sites.
 *
 * Contains:
 * - ScopeContext — the context object (the scope Layouts provide it)
 * - useScope — read the active { customer, siteGroup, site } scope (plain JS)
 */
import { createContext, useContext } from 'react';

const ScopeContext = createContext(null);

/**
 * Read the active route scope seeded by the enclosing scope Layout. All values
 * are plain JS objects (or null when that tier is not in scope).
 *
 * @returns {{ customer?: object, siteGroup?: object, site?: object }}
 */
export function useScope() {
  const scope = useContext(ScopeContext);
  if (scope == null) {
    throw new Error('useScope() must be used within a scope Layout (CustomerLayout / SiteGroupLayout / SiteLayout).');
  }
  return scope;
}

export default ScopeContext;
